import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { useNoticeBranding } from "@/hooks/use-notice-branding";
import { useAppStore } from "@/store";
import { PageHeader, PageSkeleton, Tabs, type TabDef } from "@/components/ui";
import { GeneralTab } from "@/components/org-settings/general-tab";
import { LettersTab } from "@/components/org-settings/letters-tab";
import { ReportFormsTab } from "@/components/org-settings/report-forms-tab";
import { MembersTab } from "@/components/org-settings/members-tab";
import { GroupsTab } from "@/components/org-settings/groups-tab";
import { InvitationsTab } from "@/components/org-settings/invitations-tab";
import { CustomFieldsTab } from "@/components/org-settings/custom-fields-tab";
import { ViolationSettingsTab } from "@/components/org-settings/violation-settings-tab";

type OrgSettingsTab =
  | "general"
  | "letters"
  | "report-forms"
  | "violations"
  | "members"
  | "groups"
  | "invitations"
  | "custom-fields";

const ALLOWED_TABS: OrgSettingsTab[] = [
  "general",
  "letters",
  "report-forms",
  "violations",
  "members",
  "groups",
  "invitations",
  "custom-fields",
];

interface OrgSettingsSearch {
  tab?: OrgSettingsTab;
}

function parseTab(value: unknown): OrgSettingsTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/org/settings/")({
  component: OrgSettingsPage,
  validateSearch: (search: Record<string, unknown>): OrgSettingsSearch => ({
    tab: parseTab(search.tab),
  }),
});

function OrgSettingsPage() {
  const { t } = useTranslation("org");
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const canRead = can("org_settings", "read");
  const canUpdate = can("org_settings", "update");
  const orgName = useAppStore((s) => s.activeOrgName);
  const pwsId = useNoticeBranding().data?.program_pws_id;
  // The water system's PWS ID is the program's own identifier, authored with
  // the letter program: an org that has not set one gets no subline at all.
  const subtitle = orgName && pwsId ? t("settings.pwsSubline", { org: orgName, pwsId }) : undefined;

  const activeTab: OrgSettingsTab = tab ?? "general";

  const setActiveTab = (next: OrgSettingsTab) => {
    void navigate({
      to: "/org/settings",
      // General is the default, so it clears the param instead of stacking a
      // history entry for every tab switch.
      search: next === "general" ? {} : { tab: next },
      replace: true,
    });
  };

  // `usePermissions()` reads deny-all until its envelope resolves, so the
  // redirect below has to wait for a real answer — deciding earlier bounces
  // every hard navigation (bookmark, deep link, reload) to the home route.
  if (permissionsLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Redirect callers without org-settings read access to the home route.
  if (!canRead) {
    void navigate({ to: "/" });
    return null;
  }

  const tabs: TabDef<OrgSettingsTab>[] = [
    { value: "general", label: t("settings.tabs.general") },
    { value: "letters", label: t("settings.tabs.letters") },
    { value: "report-forms", label: t("settings.tabs.reportForms") },
    { value: "violations", label: t("settings.tabs.violations") },
    { value: "members", label: t("settings.tabs.members") },
    { value: "groups", label: t("settings.tabs.groups") },
    { value: "invitations", label: t("settings.tabs.invitations") },
    { value: "custom-fields", label: t("settings.tabs.customFields") },
  ];

  return (
    <div className="p-8">
      <div className="space-y-4">
        <PageHeader title={t("settings.title")} subtitle={subtitle} />

        <div>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ariaLabel={t("settings.title")}
          />

          <div
            className="mt-6"
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {activeTab === "general" && <GeneralTab canUpdate={canUpdate} />}
            {activeTab === "letters" && <LettersTab />}
            {activeTab === "report-forms" && <ReportFormsTab />}
            {activeTab === "violations" && <ViolationSettingsTab canUpdate={canUpdate} />}
            {activeTab === "members" && <MembersTab />}
            {activeTab === "groups" && <GroupsTab />}
            {activeTab === "invitations" && <InvitationsTab />}
            {activeTab === "custom-fields" && <CustomFieldsTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
